
/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { Button } from "@mui/material";
import { useAppSnackbarState } from "../../../../../Basis/Components/AppSnackbarContext";
import CheckboxInput from "../../../../../Basis/Components/CheckboxInput";
import ColorPicker from "../../../../../Basis/Components/ColorPicker";
import FileUpload from "../../../../../Basis/Components/FileUploadBase/FileUpload";
import ImageFileUploadInput from "../../../../../Basis/Components/ImageFileUploadInput";
import LoadingIndicator from "../../../../../Basis/Components/LoadingIndicator";
import LokalisierterTextInput from "../../../../../Basis/Components/LokalisierterTextInput";
import TextInput from "../../../../../Basis/Components/TextInput";
import { nameof } from "../../../../../Basis/Utils/AnyUtil";
import NumberUtil from "../../../../../Basis/Utils/NumberUtil";
import useExecute from "../../../../../Basis/Utils/useExecute";
import UIConstants from "../../../../../UIConstants";
import AendereTeilnahmeEinstellungen, { AendereTeilnahmeEinstellungenInput } from "../../../../Teilnahmen/Commands/AendereTeilnahmeEinstellungen";
import SchriftartSelect from "../../../../Teilnahmen/Components/SchriftartSelect";
import LadeTeilnahmeEinstellungen from "../../../../Teilnahmen/Queries/LadeTeilnahmeEinstellungen";
import Logo from "../../../../Teilnahmen/Queries/Logo";
import Schriftart from "../../../../Teilnahmen/Queries/Schriftart";
import TeilnahmeEinstellungen from "../../../../Teilnahmen/Queries/TeilnahmeEinstellungen";
import { translationKeys, useTranslation } from "../../../Internationalisierung/i18nEinstellungen";
import ActionsBar from "../ActionsBar";
import { useSchnelleinstiegContext } from "../SchnelleinstiegContext";
import SchrittCount from "../SchrittCount";
import SchrittHeader from "../SchrittHeader";
import SchrittText from "../SchrittText";
import SchrittTitel from "../SchrittTitel";
import { QueryResult } from "../utils";
import { FELDER_GRUPPEN, FelderGruppe } from "./types";

export type Result = {
    input: AendereTeilnahmeEinstellungenInput,
    banner?: FileUpload,
    logoAufHintergrund?: FileUpload,
    logo?: FileUpload,
    favicon?: FileUpload,
}

const ANZAHL_DATEIEN = 1;

export default function SchnelleinstiegBranding() {
    const { t } = useTranslation();
    const { WeiterButton } = useSchnelleinstiegContext();
    const [gruppenIndex, setGruppenIndex] = React.useState(0);
    const aktuelleGruppe = FELDER_GRUPPEN[gruppenIndex];
    const [result, setResult] = React.useState<QueryResult<Result>>({ loading: true });
    const { items } = useAppSnackbarState();
    const kannZuFehlerSpringen = React.useRef(true);
    const aendereTeilnahmeEinstellungen = useExecute(new AendereTeilnahmeEinstellungen([]));

    function getData({ banner, logo, logoAufHintergrund, favicon, faviconBlobId, ...restData }: TeilnahmeEinstellungen): Result {
        const logoAufHintergrundFile = toFileUpload(logoAufHintergrund);
        const logoFile = toFileUpload(logo);
        const bannerFile = toFileUpload(banner);
        const faviconFile = toFileUpload(favicon);

        return {
            logoAufHintergrund: logoAufHintergrundFile,
            logo: logoFile,
            banner: bannerFile,
            favicon: faviconFile,
            input: {
                ...restData,
                logoAufHintergrundDateiname: logoAufHintergrund?.dateiname,
                logoAufHintergrundDateiBase64: logoAufHintergrundFile ? NumberUtil.toBase64String(logoAufHintergrundFile.data) : undefined,
                logoDateiname: logo?.dateiname,
                logoDateiBase64: logoFile ? NumberUtil.toBase64String(logoFile.data) : undefined,
                geschlechtDiversAnzeigen: restData.geschlechtDiversAnzeigen,
                bannerDateiname: banner?.dateiname,
                bannerBase64: bannerFile ? NumberUtil.toBase64String(bannerFile.data) : undefined,
                bannerNurAufSucheAnzeigen: restData.bannerNurAufSucheAnzeigen,
                faviconBase64: faviconFile ? NumberUtil.toBase64String(faviconFile.data) : undefined,
                faviconDateiname: favicon?.dateiname,
            }
        };
    }

    new LadeTeilnahmeEinstellungen((data) => {
        const daten = getData(data);
        setResult({ loading: false, data: daten });
    });

    React.useEffect(() => {
        if (!kannZuFehlerSpringen.current) {
            return;
        }

        const fehlerPaths = items
            .filter(item => item.level === "Error")
            .map(item => item.path.slice(-1)[0].toLowerCase());

        if (fehlerPaths.length <= 0) {
            return;
        }

        let gruppe: FelderGruppe = aktuelleGruppe;
        if (fehlerPaths.includes(nameof<AendereTeilnahmeEinstellungenInput>("schriftart"))) {
            gruppe = "schriftart";
        } else if (fehlerPaths.some(fp => [
            nameof<AendereTeilnahmeEinstellungenInput>("sekundaerfarbe"),
            nameof<AendereTeilnahmeEinstellungenInput>("schriftfarbe"),
        ].includes(fp))) {
            gruppe = "farben";
        } else if (fehlerPaths.some(fp => [
            nameof<AendereTeilnahmeEinstellungenInput>("logoAufHintergrundDateiBase64"),
            nameof<AendereTeilnahmeEinstellungenInput>("logoDateiBase64"),
            nameof<AendereTeilnahmeEinstellungenInput>("bannerBase64"),
        ].includes(fp))) {
            gruppe = "logos";
        } else if (fehlerPaths.some(fp => [
            nameof<AendereTeilnahmeEinstellungenInput>("bannerBase64"),
            nameof<AendereTeilnahmeEinstellungenInput>("bannerNurAufSucheAnzeigen")
        ].includes(fp))) {
            gruppe = "banner";
        } else if (fehlerPaths.some(fp => [
            nameof<AendereTeilnahmeEinstellungenInput>("faviconBase64"),
            nameof<AendereTeilnahmeEinstellungenInput>("faviconDateiname"),
        ].includes(fp))) {
            gruppe = "titel"
        } else if (fehlerPaths.some(fp => [
            nameof<AendereTeilnahmeEinstellungenInput>("agbUrl"),
            nameof<AendereTeilnahmeEinstellungenInput>("datenschutzUrl"),
            nameof<AendereTeilnahmeEinstellungenInput>("impressumUrl"),
            nameof<AendereTeilnahmeEinstellungenInput>("fusstext"),
        ].includes(fp))) {
            gruppe = "seitenfuss"
        }

        if (gruppe !== aktuelleGruppe) {
            setGruppenIndex(FELDER_GRUPPEN.findIndex(g => g === gruppe));
        }

        kannZuFehlerSpringen.current = false;
    }, [items, aktuelleGruppe]);

    function handleWeiterClick() {
        setGruppenIndex(prev => Math.min(prev + 1, FELDER_GRUPPEN.length - 1));
    }

    function handleZurueckClick() {
        setGruppenIndex(prev => Math.max(prev - 1, 0));
    }

    function handleChangeInput<K extends keyof AendereTeilnahmeEinstellungenInput>(key: K) {
        return (value: AendereTeilnahmeEinstellungenInput[K]) => {
            setResult(prev => {
                if ("data" in prev) {
                    return {
                        ...prev,
                        data: {
                            ...prev.data,
                            input: {
                                ...prev.data.input,
                                [key]: value,
                            }
                        }
                    }
                }

                return prev;
            })
        };
    }

    function handleImageFileUploadInputChange(key: "logo" | "logoAufHintergrund" | "banner" | "favicon") {
        return (files: FileUpload[]) => {
            setResult(prev => {
                if ("data" in prev) {
                    if (key in prev.data) {
                        return {
                            ...prev,
                            data: {
                                ...prev.data,
                                [key]: files[0]
                            }
                        }
                    }
                }

                return prev;
            })
        }
    }

    if (result.loading) {
        return <LoadingIndicator loading />;
    }

    return (
        <div>
            <SchrittHeader>
                <SchrittTitel>
                    {t(translationKeys.schnelleinstiegBranding.titel[aktuelleGruppe])}
                </SchrittTitel>
            </SchrittHeader>

            <SchrittText>{t(translationKeys.schnelleinstiegBranding.text[aktuelleGruppe])}</SchrittText>
            {(() => {
                switch (aktuelleGruppe) {
                    case "schriftart":
                        return (
                            <SchriftartSelect
                                caption={t(translationKeys.schnelleinstiegBranding.schriftart)}
                                value={result.data.input.schriftart}
                                onChange={v => handleChangeInput("schriftart")(v as Schriftart)}
                                typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("schriftart")}
                            />
                        );
                    case "farben":
                        return (
                            <>
                                <ColorPicker
                                    caption={t(translationKeys.schnelleinstiegBranding.primaerfarbe)}
                                    value={result.data.input.primaerfarbe ?? ""}
                                    onChange={handleChangeInput("primaerfarbe")}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("primaerfarbe")}
                                    tooltip={t(translationKeys.schnelleinstiegBranding.primaerfarbeTooltip)}
                                />
                                <ColorPicker
                                    caption={t(translationKeys.schnelleinstiegBranding.sekundaerfarbe)}
                                    value={result.data.input.sekundaerfarbe ?? ""}
                                    onChange={handleChangeInput("sekundaerfarbe")}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("sekundaerfarbe")}
                                    tooltip={t(translationKeys.schnelleinstiegBranding.sekundaerfarbeTooltip)}
                                />
                                <ColorPicker
                                    caption={t(translationKeys.schnelleinstiegBranding.schriftfarbe)}
                                    value={result.data.input.schriftfarbe ?? ""}
                                    onChange={handleChangeInput("schriftfarbe")}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("schriftfarbe")}
                                    tooltip={t(translationKeys.schnelleinstiegBranding.schriftfarbeTooltip)}
                                />
                            </>
                        );
                    case "logos":
                        return (
                            <>
                                <ImageFileUploadInput
                                    caption={t(translationKeys.schnelleinstiegBranding.logoAufHintergrund)}
                                    allowedFileExtensions={UIConstants.erlaubteDateiendungenBild}
                                    altImage={t(translationKeys.schnelleinstiegBranding.logoAufHintergrund)}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("logoAufHintergrundDateiBase64")}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    maxFileSize={UIConstants.maxFileSize}
                                    numberFiles={ANZAHL_DATEIEN}
                                    value={result.data.logoAufHintergrund ? [result.data.logoAufHintergrund] : []}
                                    onChange={handleImageFileUploadInputChange("logoAufHintergrund")}
                                    tooltip={t(translationKeys.schnelleinstiegBranding.logoTooltip)}
                                    previewBackgroundColor={result.data.input.sekundaerfarbe}
                                />
                                <ImageFileUploadInput
                                    caption={t(translationKeys.schnelleinstiegBranding.logo)}
                                    allowedFileExtensions={UIConstants.erlaubteDateiendungenBild}
                                    altImage={t(translationKeys.schnelleinstiegBranding.logo)}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("logoDateiBase64")}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    maxFileSize={UIConstants.maxFileSize}
                                    numberFiles={ANZAHL_DATEIEN}
                                    value={result.data.logo ? [result.data.logo] : []}
                                    onChange={handleImageFileUploadInputChange("logo")}
                                    tooltip={t(translationKeys.schnelleinstiegBranding.logoTooltip)}
                                />
                            </>
                        );
                    case "banner":
                        return (
                            <>
                                <ImageFileUploadInput
                                    allowedFileExtensions={UIConstants.erlaubteDateiendungenBild}
                                    caption={t(translationKeys.schnelleinstiegBranding.banner)}
                                    altImage={t(translationKeys.schnelleinstiegBranding.banner)}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("bannerBase64")}
                                    maxFileSize={UIConstants.maxFileSize}
                                    numberFiles={ANZAHL_DATEIEN}
                                    value={result.data.banner ? [result.data.banner] : []}
                                    onChange={handleImageFileUploadInputChange("banner")}
                                    tooltip={t(translationKeys.schnelleinstiegBranding.bannerTooltip)}
                                />

                                <CheckboxInput
                                    caption={t(translationKeys.schnelleinstiegBranding.bannerNurAufSucheAnzeigen)}
                                    value={result.data.input.bannerNurAufSucheAnzeigen}
                                    onChange={handleChangeInput("bannerNurAufSucheAnzeigen")}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("bannerNurAufSucheAnzeigen")}
                                />
                            </>
                        );
                    case "titel":
                        return (
                            <>
                                <ImageFileUploadInput
                                    allowedFileExtensions={UIConstants.erlaubteDateiendungenFavicon}
                                    caption={t(translationKeys.schnelleinstiegBranding.favicon)}
                                    altImage={t(translationKeys.schnelleinstiegBranding.favicon)}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("faviconBase64")}
                                    maxFileSize={1024 * 1024}
                                    numberFiles={1}
                                    value={result.data.favicon !== undefined ? [result.data.favicon] : []}
                                    onChange={handleImageFileUploadInputChange("favicon")}
                                    tooltip={t(translationKeys.schnelleinstiegBranding.faviconTooltip)}
                                />
                                <LokalisierterTextInput
                                    captions={{
                                        de: t(translationKeys.schnelleinstiegBranding.homepageTitel),
                                        en: t(translationKeys.schnelleinstiegBranding.homepageTitelEN),
                                    }}
                                    feldBezeichnungen={{
                                        de: nameof<AendereTeilnahmeEinstellungenInput>("homepageTitel"),
                                        en: nameof<AendereTeilnahmeEinstellungenInput>("homepageTitelEN")
                                    }}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    values={{
                                        de: result.data.input.homepageTitel ?? "",
                                        en: result.data.input.homepageTitelEN ?? "",
                                    }}
                                    onChange={(e) => setResult(prev => "data" in prev
                                        ? {
                                            ...prev,
                                            data: {
                                                ...prev.data,
                                                input: {
                                                    ...prev.data.input,
                                                    homepageTitel: e.de,
                                                    homepageTitelEN: e.en
                                                }
                                            }
                                        }
                                        : prev)}
                                />
                            </>)
                    case "seitenfuss":
                        return (
                            <>
                                <TextInput
                                    value={result.data.input.agbUrl}
                                    onChange={e => handleChangeInput("agbUrl")(e.target.value)}
                                    caption={t(translationKeys.schnelleinstiegBranding.agbUrl)}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("agbUrl")}
                                />
                                <TextInput
                                    value={result.data.input.datenschutzUrl}
                                    onChange={e => handleChangeInput("datenschutzUrl")(e.target.value)}
                                    caption={t(translationKeys.schnelleinstiegBranding.datenschutzUrl)}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("datenschutzUrl")}
                                />
                                <TextInput
                                    value={result.data.input.impressumUrl}
                                    onChange={e => handleChangeInput("impressumUrl")(e.target.value)}
                                    caption={t(translationKeys.schnelleinstiegBranding.impressumUrl)}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("impressumUrl")}
                                />
                                <TextInput
                                    value={result.data.input.fusstext}
                                    onChange={e => handleChangeInput("fusstext")(e.target.value)}
                                    caption={t(translationKeys.schnelleinstiegBranding.fusstext)}
                                    typBezeichnung={AendereTeilnahmeEinstellungen.commandName}
                                    feldBezeichnung={nameof<AendereTeilnahmeEinstellungenInput>("fusstext")}
                                    multiline
                                    rows={3}
                                />
                            </>
                        );
                }
            })()}
            <ActionsBar>
                {gruppenIndex > 0 && (
                    <Button
                        variant="text"
                        color="secondary"
                        onClick={handleZurueckClick}
                    >
                        Zurück
                    </Button>
                )}
                <SchrittCount akutellerSchritt={gruppenIndex + 1} schritteGesamt={FELDER_GRUPPEN.length} />
                {gruppenIndex < FELDER_GRUPPEN.length - 1 ? (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleWeiterClick}
                    >
                        Weiter
                    </Button>
                ) : (
                    <WeiterButton
                        onClick={() => {
                            kannZuFehlerSpringen.current = true;
                            return aendereTeilnahmeEinstellungen({
                                ...result.data.input,
                                logoDateiname: result.data.logo?.name,
                                logoDateiBase64: result.data.logo ? NumberUtil.toBase64String(result.data.logo.data) : undefined,
                                logoAufHintergrundDateiname: result.data.logoAufHintergrund?.name,
                                logoAufHintergrundDateiBase64: result.data.logoAufHintergrund ? NumberUtil.toBase64String(result.data.logoAufHintergrund.data): undefined,
                                bannerDateiname: result.data.banner?.name,
                                bannerBase64: result.data.banner ? NumberUtil.toBase64String(result.data.banner.data) : undefined,
                                faviconDateiname: result.data.favicon?.name,
                                faviconBase64: result.data.favicon ? NumberUtil.toBase64String(result.data.favicon.data) : undefined,
                            });
                        }}
                    />
                )}
            </ActionsBar>
        </div>
    )
}

function toFileUpload(logo?: Logo): FileUpload | undefined {
    if (logo) {
        return {
            contentType: logo.contentType,
            name: logo.dateiname,
            data: getLogoDaten(logo.logoDaten),
        };
    }

    return undefined;
}

function getLogoDaten(daten: string | Array<number>): Array<number> {
    if (Array.isArray(daten)) {
        return daten;
    }

    const binaryString = window.atob(daten);
    const length = binaryString.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    return Array.from(bytes);
}